import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus } from "lucide-react";
import { Reveal, SectionLabel, EASE } from "@/components/Reveal";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";

const FAQ = [
  {
    q: "Faut-il déjà être à l'aise dans l'eau ?",
    a: "Trois prérequis seulement : avoir reçu au moins une séance de Janzu, avoir 18 ans minimum et être physiquement apte à se mouvoir librement. La crainte de l'eau ne bloque pas la formation — des spécialisations lui sont même dédiées.",
  },
  {
    q: "La formation est-elle finançable ?",
    a: "L'école est certifiée Qualiopi : le cursus praticien ouvre l'accès aux financements de la formation professionnelle. Contactez-nous pour constituer votre dossier.",
  },
  {
    q: "En combien de temps devient-on praticien·ne ?",
    a: "Le cursus praticien se compose de trois modules de cinq jours chacun — Janzu I Surface, Janzu II Immersion, Janzu III Accompagnement — plus un temps de pratique personnelle entre les modules. Le Master Pro porte le parcours au niveau Praticien Expert.",
  },
  {
    q: "Où se déroulent les formations ?",
    a: "Dans des bassins privés chauffés à environ 33 °C, principalement en Charente-Maritime (La Rochelle), et également sur l'île de La Réunion et à Maurice.",
  },
];

export default function Faq() {
  const [faqOpen, setFaqOpen] = useState(0);
  const { tr } = useLang();

  return (
    <main className="min-h-screen bg-abyss pt-[72px] pb-24" data-testid="faq-page">
      <div className="mx-auto max-w-4xl px-5 pt-14 md:px-10">
        <Reveal>
          <SectionLabel>{tr("Questions fréquentes")}</SectionLabel>
          <h1 className="mt-5 font-serif text-4xl md:text-5xl font-light text-foam">
            {tr("Tout savoir avant de plonger")}
          </h1>
        </Reveal>
        <div className="mt-12">
          {FAQ.map((item, i) => (
            <Reveal key={i} delay={0.05 * i}>
              <div className="border-b border-aqua/12 first:border-t" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}
                  className="flex w-full items-center justify-between gap-6 py-6 text-left"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-serif text-xl md:text-2xl text-foam/90">{tr(item.q)}</span>
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-aqua/30 text-aqua">
                    {faqOpen === i ? <Minus size={15} /> : <Plus size={15} />}
                  </span>
                </button>
                <motion.div
                  initial={false}
                  animate={{ height: faqOpen === i ? "auto" : 0, opacity: faqOpen === i ? 1 : 0 }}
                  transition={{ duration: 0.45, ease: EASE }}
                  className="overflow-hidden"
                >
                  <p className="pb-7 pr-10 text-sm leading-relaxed text-foam/65">{tr(item.a)}</p>
                </motion.div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="glass-card mt-14 rounded-2xl p-8 text-center">
            <p className="font-serif text-2xl text-foam">{tr("Une autre question ?")}</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-foam/60">
              {tr("Stéphanie vous répond personnellement — par téléphone ou via le formulaire.")}
            </p>
            <Link
              to="/contact"
              data-testid="faq-contact-cta"
              className="mt-6 inline-block rounded-full bg-aqua px-8 py-3.5 text-sm font-semibold text-abyss transition-all duration-300 hover:bg-foam"
            >
              {tr("Nous contacter")}
            </Link>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
